// client/constants/sorters.js

// ─── Basis ───────────────────────────────────────────────────────────────────
export const norm = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

export const fullName = (obj) =>
  [obj?.vorname, obj?.nachname].filter(Boolean).join(" ").trim();

export const kundenName = (obj) => {
  if (obj?.kunden_firma) return obj.kunden_firma;
  const n = [obj?.kunden_vorname, obj?.kunden_nachname].filter(Boolean).join(" ");
  return n || "";
};

// robustes Timestamp-Parsing für Erstelldatum (created_at / erstellt_am / id Fallback)
export const timeKey = (x) => {
  const v = x?.created_at ?? x?.erstellt_am ?? x?.id ?? 0;
  const t = typeof v === "string" ? Date.parse(v) : Number(v);
  return Number.isFinite(t) ? t : 0;
};

// Timestamp für letzte Aktualisierung (aktualisiert_am / updated_at / Fallback auf erstellt_am)
export const updateKey = (x) => {
  const v = x?.aktualisiert_am ?? x?.updated_at ?? x?.erstellt_am ?? x?.created_at ?? x?.id ?? 0;
  const t = typeof v === "string" ? Date.parse(v) : Number(v);
  return Number.isFinite(t) ? t : 0;
};

// String/Number/Date -> ms oder 0
export const parseDateKey = (v) => {
  if (v == null) return 0;
  const t = Date.parse(String(v));
  if (!Number.isNaN(t)) return t;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ISO/Datum-only sicher parsen -> ms|null
export const parseIsoSmart = (s) => {
  if (s == null) return null;
  let v = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) v = `${v}T12:00:00`;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
};

// Termin aus diversen Feldern herausfischen -> Date|null
export const parseTermin = (obj) => {
  const direct = obj?.termin ?? obj?.termin_start ?? obj?.abnahme_datum_iso ?? obj?.abnahme_datum ?? null;
  if (direct != null) {
    const d = new Date(direct);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const dStr = obj?.termin_datum ?? obj?.datum ?? obj?.date ?? obj?.terminTag ?? null;
  const tStr = obj?.termin_zeit ?? obj?.zeit ?? obj?.time ?? obj?.terminUhrzeit ?? null;
  if (dStr && tStr) {
    const d1 = new Date(`${dStr}T${tStr}`);
    if (!Number.isNaN(d1.getTime())) return d1;
    const d2 = new Date(`${dStr} ${tStr}`);
    if (!Number.isNaN(d2.getTime())) return d2;
  }
  if (dStr) {
    const d = new Date(dStr);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

// Vergleicher: Strings (bereits normalisiert)
export const byStr = (a, b, dir = "asc") => (dir === "asc" ? cmp(a, b) : cmp(b, a));

// Vergleicher: Zahlen/Zeiten, Nulls ans Ende
export const byNumNullsLast = (a, b, dir = "asc") => {
  const aNull = a == null, bNull = b == null;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  return dir === "asc" ? a - b : b - a;
};

// ─── Erweiterungen (empfohlen) ───────────────────────────────────────────────
export const deCHCollator = new Intl.Collator("de-CH", {
  sensitivity: "base",
  numeric: true,
});

export const cmpLocale = (a, b, dir = "asc") => {
  const A = a ?? "";
  const B = b ?? "";
  const base = deCHCollator.compare(String(A), String(B));
  return dir === "asc" ? base : -base;
};

export const byText = (a, b, dir = "asc") => cmpLocale(a, b, dir);

export const byBool = (a, b, { trueFirst = true } = {}) => {
  const av = !!a, bv = !!b;
  if (av === bv) return 0;
  return trueFirst ? (bv ? 1 : -1) : (av ? 1 : -1);
};

export const byStatus = (a, b, order = [], dir = "asc") => {
  const idx = (s) => {
    const i = order.indexOf(String(s || "").toLowerCase());
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };
  const base = idx(a) - idx(b);
  return dir === "asc" ? base : -base;
};

export const chain = (...comparators) => (A, B) => {
  for (const fn of comparators) {
    const r = fn(A, B);
    if (r !== 0) return r;
  }
  return 0;
};

export const byKeyStr = (key, dir = "asc") => (A, B) =>
  byText(norm(A?.[key]), norm(B?.[key]), dir);

export const byKeyLocale = (key, dir = "asc") => (A, B) =>
  cmpLocale(A?.[key], B?.[key], dir);

export const byKeyNumNullsLast = (key, dir = "asc") => (A, B) =>
  byNumNullsLast(A?.[key], B?.[key], dir);

export const byKeyTime = (key, dir = "asc") => (A, B) =>
  byNumNullsLast(parseDateKey(A?.[key]), parseDateKey(B?.[key]), dir);

export const byKeyStatus = (key, order, dir = "asc") => (A, B) =>
  byStatus(A?.[key], B?.[key], order, dir);

export const tieById = (dir = "asc") => (A, B) =>
  byNumNullsLast(Number(A?.id ?? 0), Number(B?.id ?? 0), dir);

export const tieByTimeKey = (dir = "desc") => (A, B) =>
  byNumNullsLast(timeKey(A), timeKey(B), dir);

export const titleOf = (row) => row?.titel || row?.name || `#${row?.id ?? ""}`.trim();
export const kundeOf = (row) => kundenName(row);

// Optional: CH-Datum "DD.MM.YYYY" -> Date
export const parseCHDate = (s) => {
  if (typeof s !== "string") return null;
  const m = s.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const t = Date.parse(`${yyyy}-${mm}-${dd}T12:00:00`);
  return Number.isFinite(t) ? new Date(t) : null;
};
